/**
 * Time-formatting helpers shared by ora's WorkflowDetailDrawer and the
 * Outlook plugin's InstanceDetailDrawer.
 *
 * US-format throughout (per the encapsulation plan):
 *   - ``formatDate(iso)`` → ``MM/DD/YYYY`` (e.g. ``05/15/2026``)
 *   - ``formatTime(iso)`` → ``h:mm a`` lowercase (e.g. ``2:45 pm``)
 *   - ``formatAbsolute(iso)`` → ``MM/DD/YYYY h:mm a`` (e.g. ``05/15/2026 2:45 pm``)
 *   - ``formatRelativeTime(date)`` — coarse relative-time chip
 *     (unchanged: "3 min ago", "2 hrs ago", ...)
 *   - ``computeDurationFromDates(startIso, endIso?)`` — delegates to ``formatDuration``
 *
 * ``date-fns`` is already on board for ora (see ``app/common/utilities/timeUtils.ts``).
 * Lowercase am/pm uses date-fns' ``aaa`` token.
 *
 * This module is also THE home of duration arithmetic for the whole workflows
 * tree — see ``decomposeDuration`` below. It is pure, React-free and Remix-free,
 * so the Outlook add-in imports it directly; ``app/common/utilities/formatDuration``
 * is a re-export of ``formatDuration`` here, not a second implementation.
 */

import { format } from "date-fns";

const PLACEHOLDER = "—";

const DATE_PATTERN = "MM/dd/yyyy";
const TIME_PATTERN = "h:mm aaa"; // aaa = lowercase am/pm in date-fns v2+
const COMBINED_PATTERN = `${DATE_PATTERN} ${TIME_PATTERN}`;

function safeFormat(
  iso: string | null | undefined,
  pattern: string,
): string {
  if (!iso) return PLACEHOLDER;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return PLACEHOLDER;
  return format(d, pattern);
}

/**
 * Format an ISO 8601 string as ``MM/DD/YYYY`` (e.g. ``05/15/2026``).
 * Returns an em-dash placeholder when the input is null / undefined / unparseable.
 */
export function formatDate(iso: string | null | undefined): string {
  return safeFormat(iso, DATE_PATTERN);
}

/**
 * Format an ISO 8601 string as ``h:mm a`` lowercase (e.g. ``2:45 pm``).
 * Returns an em-dash placeholder when the input is null / undefined / unparseable.
 */
export function formatTime(iso: string | null | undefined): string {
  return safeFormat(iso, TIME_PATTERN);
}

/**
 * Format an ISO 8601 string as ``MM/DD/YYYY h:mm a`` lowercase
 * (e.g. ``05/15/2026 2:45 pm``). Returns an em-dash placeholder when the input
 * is null / undefined / unparseable.
 */
export function formatAbsolute(iso: string | null | undefined): string {
  return safeFormat(iso, COMBINED_PATTERN);
}

/**
 * Coarse relative-time string ("just now", "3 min ago", "2 hrs ago", "1 day
 * ago"). Used in row metadata where exact precision isn't useful.
 */
export function formatRelativeTime(date: string | Date, now: Date = new Date()): string {
  const ms = now.getTime() - new Date(date).getTime();
  const mins = Math.floor(ms / 60_000);
  if (mins < 1) return "just now";
  if (mins === 1) return "1 min ago";
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs === 1) return "1 hr ago";
  if (hrs < 24) return `${hrs} hrs ago`;
  const days = Math.floor(hrs / 24);
  return days === 1 ? "1 day ago" : `${days} days ago`;
}

/**
 * ELAPSED wait age for a pinned-pane queue row, with a spelled-out text
 * equivalent for assistive technology.
 *
 *   formatElapsedWait("<45 minutes ago>")  → { label: "45m",      aria: "waiting 45 minutes" }
 *   formatElapsedWait(null)                → { label: "unknown",  aria: "wait time unknown" }
 *
 * Deliberately narrow:
 *   - ELAPSED ONLY. There is no branch that produces a remaining time, an
 *     estimate or a prediction, and the signature cannot express one — it
 *     takes a single past instant and returns two strings.
 *   - Null / absent / unparseable → the literal "unknown". NEVER "0m", never
 *     "just now": PRD ad-2 names a false-fresh age as exactly the untruth the
 *     ban exists to prevent, and a run parked before the `waiting_since`
 *     column landed reads null forever.
 *   - A `since` in the FUTURE (clock skew between the substrate and the host)
 *     clamps to "unknown" rather than rendering a negative or zero age.
 */
export function formatElapsedWait(
  sinceIso: string | null | undefined,
  now: Date = new Date(),
): { label: string; aria: string } {
  const UNKNOWN = { label: "unknown", aria: "wait time unknown" };
  if (!sinceIso) return UNKNOWN;
  const startedMs = new Date(sinceIso).getTime();
  if (Number.isNaN(startedMs)) return UNKNOWN;
  const elapsedMs = now.getTime() - startedMs;
  // Clock skew: a wait that has not started yet is not a wait of zero length,
  // it is a wait whose length we do not know.
  if (elapsedMs < 0) return UNKNOWN;

  const totalMinutes = Math.floor(elapsedMs / 60_000);
  if (totalMinutes < 1) {
    return { label: "<1m", aria: "waiting less than a minute" };
  }
  if (totalMinutes < 60) {
    return {
      label: `${totalMinutes}m`,
      aria: `waiting ${totalMinutes} ${totalMinutes === 1 ? "minute" : "minutes"}`,
    };
  }
  const totalHours = Math.floor(totalMinutes / 60);
  if (totalHours < 24) {
    const minutes = totalMinutes % 60;
    const label = minutes > 0 ? `${totalHours}h ${minutes}m` : `${totalHours}h`;
    const hoursWords = `${totalHours} ${totalHours === 1 ? "hour" : "hours"}`;
    const aria =
      minutes > 0
        ? `waiting ${hoursWords} ${minutes} ${minutes === 1 ? "minute" : "minutes"}`
        : `waiting ${hoursWords}`;
    return { label, aria };
  }
  const totalDays = Math.floor(totalHours / 24);
  const hours = totalHours % 24;
  const label = hours > 0 ? `${totalDays}d ${hours}h` : `${totalDays}d`;
  const daysWords = `${totalDays} ${totalDays === 1 ? "day" : "days"}`;
  const aria =
    hours > 0
      ? `waiting ${daysWords} ${hours} ${hours === 1 ? "hour" : "hours"}`
      : `waiting ${daysWords}`;
  return { label, aria };
}

/**
 * Millisecond duration split into whole hours / minutes / seconds.
 *
 * THE ONE PLACE THIS ARITHMETIC LIVES
 * -----------------------------------
 * Four independent copies of it used to exist — this file,
 * ``app/common/utilities/formatDuration.ts``, ``stepTooltipParts.ts`` (twice)
 * and ``decisionLatency.ts`` — and they had already drifted: two truncated the
 * seconds with ``Math.floor`` and two rounded, so 1 900 ms rendered "1s" on the
 * run-record page and "2s" in the step tooltip beside it, for the same run.
 *
 * ROUNDING IS THE RULE. A duration string is a reading for a human, not an
 * index: 1 900 ms is nearer two seconds than one, and truncation understates
 * every value it touches. The callers below vary only their OUTPUT TEMPLATE
 * around this decomposition; none of them re-derive it.
 */
export interface DurationParts {
  /** Whole seconds across the entire duration, ROUNDED. */
  totalSeconds: number;
  /** Whole minutes across the entire duration. */
  totalMinutes: number;
  /** Hours component. */
  hours: number;
  /** Minutes component, 0–59. */
  minutes: number;
  /** Seconds component, 0–59. */
  seconds: number;
}

export function decomposeDuration(ms: number): DurationParts {
  const safeMs = Number.isFinite(ms) && ms > 0 ? ms : 0;
  const totalSeconds = Math.round(safeMs / 1000);
  const totalMinutes = Math.floor(totalSeconds / 60);
  return {
    totalSeconds,
    totalMinutes,
    hours: Math.floor(totalMinutes / 60),
    minutes: totalMinutes % 60,
    seconds: totalSeconds % 60,
  };
}

/**
 * Format a millisecond duration as a compact, human-readable string.
 *
 *   400 ms  → "<1s"
 *   1 900  → "2s"
 *   45 000  → "45s"
 *   90 000  → "1m 30s"
 *   3 600 000 → "1h"
 *   5 400 000 → "1h 30m"
 *
 * Sub-second stays "<1s" rather than the rounded "0s"/"1s": this formatter's
 * callers render a run's total elapsed time, where "0s" reads as a bug and "1s"
 * asserts a precision the number does not have.
 */
export function formatDuration(ms: number): string {
  if (ms < 1000) return "<1s";
  const { totalSeconds, totalMinutes, hours, minutes, seconds } =
    decomposeDuration(ms);
  if (totalSeconds < 60) return `${totalSeconds}s`;
  if (totalMinutes < 60)
    return seconds > 0 ? `${totalMinutes}m ${seconds}s` : `${totalMinutes}m`;
  return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
}

/**
 * Compute the elapsed duration between two ISO timestamps, formatted via
 * ``formatDuration``. Defaults the end time to ``Date.now()`` so callers can
 * use this for an in-progress workflow.
 */
export function computeDurationFromDates(
  startIso: string,
  endIso?: string | null,
): string {
  const start = new Date(startIso).getTime();
  const end = endIso ? new Date(endIso).getTime() : Date.now();
  return formatDuration(Math.max(end - start, 0));
}
